import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, DarkSelect, Input, Modal, PageHeader, Pill } from "@/components/ui";
import { SshKeyModal } from "@/components/SshKeyModal";
import { health as nodeHealth } from "@/lib/health";
import { popup, confirmPopup } from "@/components/popup";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

export interface Node {
  id: number;
  name: string;
  host: string;
  role: string;
  engine: string;
  state: string;
  ssh_host: string | null;
  ssh_port: number;
  ssh_user: string | null;
  listen_port: number;
  cloak_domain: string | null;
  max_connections: number;
  geo_allow_countries: string | null;
  country: string | null;
  tags: string | null;
  note: string | null;
  view_position: number;
  is_active: boolean;
  created_at: string;
  agent_online?: boolean;
  agent_version?: string | null;
  agent_conns?: number | null;
  last_seen?: string | null;
  health?: string;
  mem_pct?: number | null;
  cpu_pct?: number | null;
  rx_bps?: number | null;
  tx_bps?: number | null;
}

const ROLES = ["entry", "exit", "standalone"];
const ROLE_LABEL: Record<string, string> = {
  entry: "мост / вход",
  exit: "выход",
  standalone: "одиночка",
};
// mtproto_ad убран из выбора: подпись «ad-docker» вводила в заблуждение — по коду
// он деплоится как обычный mtproto.zig, а не docker. Для docker-выхода — mtproto_docker.
const ENGINES = ["mtproto_zig", "mtproto_docker", "xray_bridge", "alexbers"];
const ENGINE_LABEL: Record<string, string> = {
  alexbers: "alexbers",
  mtproto_ad: "mtproto.zig (legacy ad)",
  mtproto_zig: "mtproto.zig (выход)",
  mtproto_docker: "docker-mtproxy (ad-выход)",
  xray_bridge: "xray-bridge (вход/мост)",
};
const ROLE_DEFAULT_ENGINE: Record<string, string> = {
  entry: "xray_bridge",
  exit: "mtproto_zig",
  standalone: "alexbers",
};
// cloak SNI (FakeTLS) осмыслен для mtproto.zig и docker-mtproxy (выход маскируется под сайт).
const ENGINE_NEEDS_CLOAK = (engine: string) =>
  engine === "mtproto_zig" || engine === "mtproto_docker";
const COUNTRIES = ["RU", "KZ", "BY", "DE", "NL", "FI", "FR", "GB", "US", "PL", "EE", "SE", "TR", "AE"];

export function flag(cc?: string | null): string {
  if (!cc || cc.length !== 2 || !/^[a-zA-Z]{2}$/.test(cc)) return "🌐";
  return String.fromCodePoint(
    ...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)),
  );
}
function roleTone(role: string): "success" | "warning" | "neutral" {
  return role === "entry" ? "warning" : role === "exit" ? "success" : "neutral";
}
function parseTags(tags: string | null): string[] {
  return (tags || "").split(",").map((t) => t.trim()).filter(Boolean);
}

export default function Nodes() {
  const [formOpen, setFormOpen] = useState(false);
  const [editNode, setEditNode] = useState<Node | null>(null);
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [keyOpen, setKeyOpen] = useState(false);
  const qc = useQueryClient();

  const q = useQuery<{ nodes: Node[] }>({
    queryKey: ["nodes"],
    queryFn: () => api("/api/admin/nodes"),
    refetchInterval: 5000,
  });
  const all = q.data?.nodes ?? [];
  const nodes = roleFilter ? all.filter((n) => n.role === roleFilter) : all;

  const health = useQuery<{
    items: { node_id: number; reachable: boolean; latency_ms: number | null }[];
  }>({
    queryKey: ["nodes-health"],
    queryFn: () => api("/api/admin/nodes/health-batch", { method: "POST" }),
    refetchInterval: 20000,
    enabled: all.length > 0,
  });
  const healthMap = new Map((health.data?.items ?? []).map((i) => [i.node_id, i]));

  // Вердикты реконсиляции (кэш свипа): ожидаемое (БД) vs факт (что реально крутится).
  const verdicts = useQuery<{ verdicts: Record<string, string> }>({
    queryKey: ["nodes-verdicts"],
    queryFn: () => api("/api/admin/nodes/verdicts"),
    refetchInterval: 15000,
    enabled: all.length > 0,
  });
  const verdictMap = verdicts.data?.verdicts ?? {};

  const stats = {
    total: all.length,
    up: all.filter((n) => n.health === "up").length,
    degraded: all.filter((n) => n.health === "down" || n.health === "degraded").length,
    offline: all.filter((n) => n.health === "no_agent" || n.health === "undeployed" || n.health === "disabled").length,
    problems: all.filter((n) => ["orphan", "empty", "drift"].includes(verdictMap[String(n.id)] || "")).length,
  };

  const openCreate = () => {
    setEditNode(null);
    setFormOpen(true);
  };
  const openEdit = (n: Node) => {
    setEditNode(n);
    setFormOpen(true);
  };
  const refresh = () => qc.invalidateQueries({ queryKey: ["nodes"] });

  const roleTabs: [string, string][] = [
    ["", "Все"],
    ["entry", "Мосты"],
    ["exit", "Выходы"],
    ["standalone", "Одиночки"],
  ];

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title="Ноды"
        right={
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <Button variant="ghost" onClick={() => setKeyOpen(true)}>
              🔑 SSH-ключ
            </Button>
            <Button variant="ghost" onClick={refresh}>
              ⟳ Обновить
            </Button>
            <Button variant="primary" onClick={openCreate}>
              + Нода
            </Button>
          </div>
        }
      />

      {/* stat header — сводка по здоровью */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatTile label="Всего нод" value={stats.total} />
        <StatTile label="Работают" value={stats.up} tone="emerald" />
        <StatTile label="Расхождения с конфигурацией" value={stats.problems} tone={stats.problems ? "rose" : "default"} />
        <StatTile label="Не на связи / без софта" value={stats.offline} tone="amber" />
      </section>

      <div className="inline-flex items-center gap-1 mb-4 p-1 rounded-xl bg-white/[0.03] border border-white/[0.06] flex-wrap">
        {roleTabs.map(([k, label]) => (
          <button
            key={k || "all"}
            onClick={() => setRoleFilter(k)}
            className={`h-8 px-3.5 rounded-lg text-sm font-medium transition-all ${
              roleFilter === k ? "bg-brand-soft text-brand glow-ring" : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {q.isLoading && <div className="text-zinc-400">Загрузка…</div>}
      {!q.isLoading && nodes.length === 0 && (
        <div className="text-zinc-500 max-w-2xl">
          Нод пока нет. Нода — VPS с ролью (<b>мост</b> — вход под доменом, <b>выход</b> — за
          рубежом, <b>одиночка</b>) и движком. Из нод собираются хосты/эндпоинты.
        </div>
      )}

      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {nodes.map((n) => (
          <NodeCard
            key={n.id}
            node={n}
            health={healthMap.get(n.id)}
            verdict={verdictMap[String(n.id)]}
            onEdit={() => openEdit(n)}
            onChanged={refresh}
          />
        ))}
      </section>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editNode ? `Нода · ${editNode.name}` : "Новая нода"}
        maxWidth="max-w-2xl"
      >
        <NodeForm
          node={editNode}
          onDone={() => {
            setFormOpen(false);
            refresh();
          }}
          onCancel={() => setFormOpen(false)}
        />
      </Modal>

      <SshKeyModal open={keyOpen} onClose={() => setKeyOpen(false)} />
    </div>
  );
}

function StatTile({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number;
  tone?: "default" | "emerald" | "amber" | "rose";
}) {
  const c =
    tone === "emerald" ? "text-emerald-300"
    : tone === "amber" ? "text-amber-300"
    : tone === "rose" ? "text-rose-300"
    : "text-zinc-50";
  return (
    <div className="rounded-2xl card-elev px-4 py-3">
      <div className="text-[11px] uppercase tracking-[0.12em] text-zinc-500">{label}</div>
      <div className={`text-2xl font-bold tabular-nums mt-0.5 ${c}`}>{value}</div>
    </div>
  );
}

export function NodeCard({
  node: n,
  health,
  verdict,
  onEdit,
  onChanged,
}: {
  node: Node;
  health?: { reachable: boolean; latency_ms: number | null };
  verdict?: string;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const [menu, setMenu] = useState(false);
  const [consoleOpen, setConsoleOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menu) return;
    const h = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as any)) setMenu(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menu]);

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/api/admin/nodes/${n.id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: onChanged,
  });
  const del = useMutation({
    mutationFn: () => api(`/api/admin/nodes/${n.id}`, { method: "DELETE" }),
    onSuccess: onChanged,
  });

  const tags = parseTags(n.tags);

  const hs = nodeHealth(n.health);
  return (
    <div className={`relative rounded-2xl card-elev p-4 hover:border-white/[0.14] transition-colors group ring-1 ring-inset ${hs.ring}`}>
      <div className="flex items-start gap-3">
        <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${hs.dot}`} title={hs.label} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-lg leading-none">{flag(n.country)}</span>
            <button
              onClick={() => setConsoleOpen(true)}
              className="font-semibold text-zinc-100 truncate hover:text-brand transition-colors"
              title="открыть ноду"
            >
              {n.name}
            </button>
            {(() => {
              const cs = combinedStatus(n.health, verdict);
              return (
                <span className={`text-[11px] font-medium ${cs.text}`} title={cs.sub || cs.label}>
                  {cs.icon} {cs.short}
                </span>
              );
            })()}
            {!n.is_active && <Pill tone="danger">off</Pill>}
          </div>
          <div className="font-mono text-xs text-zinc-500 mt-0.5 truncate">{n.host}</div>
        </div>
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenu((v) => !v)}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-zinc-100 hover:bg-white/[0.06] transition-colors"
            aria-label="Действия"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="1.6" />
              <circle cx="12" cy="12" r="1.6" />
              <circle cx="12" cy="19" r="1.6" />
            </svg>
          </button>
          {menu && (
            <div className="absolute right-0 top-9 z-20 min-w-[180px] rounded-xl card-elev shadow-pop py-1 text-sm">
              <MenuItem onClick={() => { setMenu(false); setConsoleOpen(true); }}>🖥 Консоль / управление</MenuItem>
              <MenuItem onClick={() => { setMenu(false); onEdit(); }}>Редактировать</MenuItem>
              <MenuItem onClick={() => { setMenu(false); navigator.clipboard?.writeText(n.host); }}>
                Копировать адрес
              </MenuItem>
              <MenuItem
                onClick={() => { setMenu(false); patch.mutate({ is_active: !n.is_active }); }}
              >
                {n.is_active ? "Отключить" : "Включить"}
              </MenuItem>
              <div className="my-1 border-t border-white/[0.06]" />
              <MenuItem
                danger
                onClick={async () => {
                  setMenu(false);
                  if (await confirmPopup({ title: "Удалить ноду?", body: n.name, danger: true, confirmLabel: "Удалить" }))
                    del.mutate();
                }}
              >
                Удалить
              </MenuItem>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap mt-3">
        <Pill tone={roleTone(n.role)}>{ROLE_LABEL[n.role] ?? n.role}</Pill>
        <Pill tone="neutral">{ENGINE_LABEL[n.engine] ?? n.engine}</Pill>
        <span className="text-xs text-zinc-600 font-mono">:{n.listen_port}</span>
        {n.cloak_domain && (
          <span className="text-xs text-zinc-600 font-mono truncate">cloak={n.cloak_domain}</span>
        )}
      </div>

      {tags.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mt-2">
          {tags.map((t) => (
            <span key={t} className="text-[11px] px-2 py-0.5 rounded-md bg-white/[0.05] text-zinc-400">
              #{t}
            </span>
          ))}
        </div>
      )}

      {n.agent_online && (
        <div className="grid grid-cols-3 gap-2 mt-3">
          <MiniBar label="RAM" pct={n.mem_pct} />
          <MiniBar label="CPU" pct={n.cpu_pct} />
          <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] px-2 py-1">
            <div className="text-[9px] uppercase tracking-wide text-zinc-600">трафик</div>
            <div className="text-[11px] text-zinc-300 leading-tight">
              <span className="text-emerald-400">↓{fmtBpsShort(n.rx_bps)}</span>{" "}
              <span className="text-sky-400">↑{fmtBpsShort(n.tx_bps)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/[0.05] text-xs text-zinc-500">
        <button
          onClick={() => setConsoleOpen(true)}
          className="font-mono truncate hover:text-brand transition-colors"
          title="открыть консоль ноды"
        >
          {n.ssh_user || "root"}@{n.ssh_host || n.host}:{n.ssh_port}
        </button>
        {n.agent_online ? (
          <button
            onClick={() => setConsoleOpen(true)}
            className="flex items-center gap-1.5 text-brand font-medium shrink-0 hover:brightness-110"
            title={`агент онлайн${n.agent_version ? " v" + n.agent_version : ""}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-brand shadow-[0_0_8px_2px_rgba(20,184,166,.5)]" />
            агент{n.agent_conns != null ? ` · ${n.agent_conns} conn` : ""}
          </button>
        ) : health ? (
          health.reachable ? (
            <span className="flex items-center gap-1.5 text-emerald-400 font-medium shrink-0" title="сервис отвечает на порту">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              сервис · {health.latency_ms === 0 ? "<1" : health.latency_ms}&nbsp;мс
            </span>
          ) : (
            <button
              onClick={() => setConsoleOpen(true)}
              className="flex items-center gap-1.5 text-amber-400/90 font-medium shrink-0 hover:text-amber-300"
              title="агент не установлен и порт молчит. Открой консоль → «Установить агент»."
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              нет агента
            </button>
          )
        ) : (
          <span className="text-zinc-600 shrink-0">проверка…</span>
        )}
      </div>

      <NodeConsole node={n} open={consoleOpen} onClose={() => setConsoleOpen(false)} onChanged={onChanged} onEdit={onEdit} />
    </div>
  );
}


interface NodeMetrics {
  online: boolean;
  uptime?: number;
  version?: string;
  engines?: Record<string, { installed?: boolean; active?: boolean; conns?: number }>;
  system?: {
    os?: string; kernel?: string; arch?: string;
    mem_total_kb?: number; mem_used_kb?: number; mem_pct?: number;
    cpu_model?: string; cpu_cores?: number; load1?: number; cpu_pct?: number;
    iface?: string; rx_total?: number; tx_total?: number; rx_bps?: number; tx_bps?: number;
    xray_version?: string; mtproto_version?: string;
  };
}

interface NodeVerdict {
  verdict: string;
  reason?: string;
  expected?: {
    bound?: boolean; endpoint?: string; role?: string; engine?: string;
    port?: number; forward_ips?: string[]; ad_tag?: string; cloak?: string;
  };
  actual?: {
    online?: boolean; active?: boolean; listen_ports?: number[]; forward?: string[];
    docker?: { tag?: string; tls_domain?: string; port?: number; image?: string };
  };
}

// Единый статус ноды: две оси — «сервис жив?» (health) и «конфиг сошёлся?»
// (verdict) — сводим в ОДНУ строку, чтобы не было каши «работает» vs «расхождение».
function combinedStatus(health: string | undefined, verdict?: string, reason?: string) {
  const T = {
    up: { text: "text-emerald-300", ring: "ring-emerald-500/20" },
    warn: { text: "text-amber-300", ring: "ring-amber-500/20" },
    down: { text: "text-rose-300", ring: "ring-rose-500/20" },
    mute: { text: "text-zinc-400", ring: "ring-white/10" },
  };
  if (!health)
    return { icon: "⚫", label: "Статус неизвестен", short: "…", ...T.mute, sub: reason, alert: false };
  if (verdict === "orphan")
    return { icon: "🟠", label: "Посторонний процесс", short: "посторонний", ...T.warn, sub: reason, alert: true };
  if (health === "disabled")
    return { icon: "⚪", label: "Выключена", short: "выключена", ...T.mute, sub: reason, alert: false };
  if (health === "no_agent" || verdict === "offline")
    return {
      icon: "⚫", label: "Агент офлайн · телеметрия недоступна", short: "агент офлайн", ...T.mute,
      sub: verdict === "ok" ? "сервис при этом жив (проверка по SSH)" : reason, alert: false,
    };
  if (health === "undeployed" || verdict === "empty")
    return { icon: "🔴", label: "Не развёрнута", short: "не развёрнута", ...T.down, sub: reason || "сервис не поднят", alert: true };
  if (health !== "up")
    return { icon: "🔴", label: "Не работает", short: "не работает", ...T.down, sub: reason, alert: true };
  // сервис работает (up):
  if (verdict === "drift")
    return { icon: "🟡", label: "Работает, но конфиг разошёлся", short: "конфиг разошёлся", ...T.warn, sub: reason, alert: true };
  return { icon: "🟢", label: "Работает — конфигурация в норме", short: "работает", ...T.up, sub: undefined, alert: false };
}

function VerdictBanner({ v, health, busy, rechecking, onRecheck, onCleanup, onDeploy }: {
  v?: NodeVerdict; health?: string; busy: string | null; rechecking?: boolean;
  onRecheck: () => void; onCleanup: () => void; onDeploy: () => void;
}) {
  if (!v) return null;
  const exp = v.expected;
  const act = v.actual;
  const isOrphan = v.verdict === "orphan";
  const isEmpty = v.verdict === "empty";
  const isDrift = v.verdict === "drift";
  const cs = combinedStatus(health, v.verdict, v.reason);
  return (
    <div className={`rounded-xl px-3.5 py-3 ring-1 ring-inset ${cs.ring} ${cs.alert ? "bg-white/[0.04]" : "bg-white/[0.02]"}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-base">{cs.icon}</span>
        <span className={`text-sm font-semibold ${cs.text}`}>{cs.label}</span>
        {cs.sub && <span className="text-xs text-zinc-400">— {cs.sub}</span>}
        <div className="ml-auto flex gap-1.5">
          <ActBtn onClick={onRecheck} busy={!!rechecking} label="Проверить" />
          {isOrphan && <ActBtn onClick={onCleanup} busy={busy === "cleanup"} label="Удалить посторонний процесс" danger />}
          {(isEmpty || isDrift || exp?.bound) && (
            <ActBtn
              onClick={onDeploy}
              busy={busy === "deploy"}
              label={isEmpty || isDrift ? "Развернуть по конфигурации" : "🚀 Переразвернуть"}
              primary
            />
          )}
        </div>
      </div>
      {(exp?.bound || act) && (
        <div className="grid grid-cols-2 gap-2 mt-2.5 text-[11px]">
          <div className="rounded-lg bg-black/20 px-2.5 py-2">
            <div className="text-zinc-500 mb-1">Ожидается по конфигурации</div>
            {exp?.bound ? (
              <div className="text-zinc-300 space-y-0.5">
                <div>{exp.role === "entry" ? "вход" : "выход"} хоста <b>{exp.endpoint}</b></div>
                <div>движок {exp.engine} · порт :{exp.port}</div>
                {exp.role === "entry" && exp.forward_ips?.length ? (
                  <div className="font-mono text-zinc-400">→ {exp.forward_ips.join(", ")}</div>
                ) : null}
              </div>
            ) : <div className="text-zinc-500">нода не привязана к хосту</div>}
          </div>
          <div className="rounded-lg bg-black/20 px-2.5 py-2">
            <div className="text-zinc-500 mb-1">Фактически на ноде</div>
            {act?.online === false ? (
              <div className="text-zinc-500">агент не на связи</div>
            ) : (
              <div className="text-zinc-300 space-y-0.5">
                <div>сервис {act?.active ? "активен" : "не запущен"}</div>
                {act?.listen_ports?.length ? <div>слушает {act.listen_ports.map((p) => `:${p}`).join(" ")}</div> : null}
                {act?.forward?.length ? <div className="font-mono text-zinc-400">→ {act.forward.join(", ")}</div> : null}
                {act?.docker?.tls_domain ? <div>cloak {act.docker.tls_domain}</div> : null}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function fmtKB(kb?: number): string {
  if (!kb) return "—";
  const mib = kb / 1024;
  return mib >= 1024 ? `${(mib / 1024).toFixed(2)} GiB` : `${Math.round(mib)} MiB`;
}
export function fmtBytes(b?: number): string {
  if (!b) return "0 B";
  const u = ["B", "KiB", "MiB", "GiB", "TiB"];
  let i = 0, v = b;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i >= 2 ? 1 : 0)} ${u[i]}`;
}
export function fmtBps(bps?: number): string {
  if (bps == null) return "—";
  const bits = bps * 8;
  if (bits >= 1e6) return `${(bits / 1e6).toFixed(2)} Mb/s`;
  if (bits >= 1e3) return `${(bits / 1e3).toFixed(0)} Kb/s`;
  return `${bits} b/s`;
}
export function fmtUptime(sec?: number): string {
  if (!sec) return "—";
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}д ${h}ч`;
  if (h > 0) return `${h}ч ${m}м`;
  return `${m}м`;
}

function NodeConsole({
  node: n,
  open,
  onClose,
  onChanged,
  onEdit,
}: {
  node: Node;
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
  onEdit: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [logs, setLogs] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Реалтайм-метрики из heartbeat агента (обновление каждые 3с пока открыто)
  const metrics = useQuery<NodeMetrics>({
    queryKey: ["node-metrics", n.id],
    queryFn: () => api<NodeMetrics>(`/api/admin/nodes/${n.id}/metrics`),
    enabled: open,
    refetchInterval: open ? 3000 : false,
  });
  // Вердикт: ожидаемое (БД) vs фактическое (что реально крутится на ноде).
  const verdict = useQuery<NodeVerdict>({
    queryKey: ["node-verdict", n.id],
    queryFn: () => api<NodeVerdict>(`/api/admin/nodes/${n.id}/verdict`),
    enabled: open,
    refetchInterval: open ? 10000 : false,
  });

  const m = metrics.data;
  const sys = m?.system ?? {};
  const _e = (n.engine || "");
  const ekey = _e.includes("xray")
    ? "xray"
    : _e.includes("docker")
      ? "docker"
      : _e.includes("alexbers")
        ? "alexbers"
        : "mtproto";
  const eng = m?.engines?.[ekey] ?? {};
  const engineVer = ekey === "xray" ? sys.xray_version : sys.mtproto_version;

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setToast(null);
    try { return await fn(); }
    catch (e) { setToast(`Ошибка: ${(e as Error).message}`); }
    finally { setBusy(null); metrics.refetch(); }
  };

  useEffect(() => {
    if (!open) { setLogs(null); setToast(null); }
  }, [open]);

  const doDeploy = () =>
    run("deploy", async () => {
      const r = await api<{ ok: boolean; error?: string; step?: { error?: string } }>(`/api/admin/nodes/${n.id}/deploy`, { method: "POST" });
      setToast(r.ok ? "✅ Установлено" : `❌ ${r.error ?? r.step?.error ?? "не удалось"}`);
      onChanged();
    });
  const doRestart = () =>
    run("restart", async () => {
      const r = await api<{ ok: boolean }>(`/api/admin/nodes/${n.id}/restart`, { method: "POST" });
      setToast(r.ok ? "✅ Сервис перезапущен" : "⚠️ Не активен после рестарта");
    });
  const doLogs = () =>
    run("logs", async () => {
      const r = await api<{ ok: boolean; logs?: string; error?: string }>(`/api/admin/nodes/${n.id}/logs?lines=200`);
      setLogs(r.ok ? r.logs ?? "(пусто)" : `Ошибка: ${r.error}`);
    });
  const doUninstall = () =>
    run("uninstall", async () => {
      if (!(await confirmPopup({ title: "Снести софт с ноды?", body: `«${n.name}»: SSH останется, конфиги/движок удалятся.`, danger: true, confirmLabel: "Снести" }))) return;
      const r = await api<{ ok: boolean }>(`/api/admin/nodes/${n.id}/uninstall`, { method: "POST" });
      setToast(r.ok ? "🧹 Софт снесён" : "⚠️ Не удалось");
      setLogs(null); onChanged();
    });
  const doCleanupOrphan = () =>
    run("cleanup", async () => {
      if (!(await confirmPopup({ title: "Удалить посторонний процесс?", body: `На ноде «${n.name}» запущен сервис, не предусмотренный конфигурацией. Удалить его?`, danger: true, confirmLabel: "Удалить" }))) return;
      const r = await api<{ ok: boolean; error?: string }>(`/api/admin/nodes/${n.id}/cleanup-orphan`, { method: "POST" });
      setToast(r.ok ? "Посторонний сервис удалён" : `Не удалось: ${r.error ?? "ошибка"}`);
      verdict.refetch(); onChanged();
    });

  const online = m?.online ?? n.agent_online;

  return (
    <Modal open={open} onClose={onClose} title={`Нода · ${n.name}`} maxWidth="max-w-4xl">
      <div className="space-y-3">
        {/* top strip — статус НЕ дублируем: он единой строкой в VerdictBanner ниже */}
        <div className="flex items-center gap-2.5 flex-wrap text-sm">
          <span className="text-lg">{flag(n.country)}</span>
          <Pill tone={roleTone(n.role)}>{ROLE_LABEL[n.role] ?? n.role}</Pill>
          <Pill tone="neutral">{ENGINE_LABEL[n.engine] ?? n.engine}</Pill>
          {sys.os && <Pill tone="neutral">{sys.arch ?? "linux"}</Pill>}
          <span className="font-mono text-xs text-zinc-500">
            {n.ssh_user || "root"}@{n.ssh_host || n.host}:{n.ssh_port} · :{n.listen_port}
          </span>
          <button
            onClick={() => { onClose(); onEdit(); }}
            className="ml-auto h-7 px-2.5 text-xs rounded-lg border border-white/[0.08] text-zinc-300 hover:bg-white/[0.05] transition-colors"
          >
            ⚙ Настройки
          </button>
        </div>

        {toast && <div className="text-sm text-zinc-100 bg-white/[0.04] rounded-lg px-3 py-2">{toast}</div>}

        <VerdictBanner
          v={verdict.data}
          health={n.health}
          busy={busy}
          rechecking={verdict.isFetching}
          onRecheck={() => verdict.refetch()}
          onCleanup={doCleanupOrphan}
          onDeploy={doDeploy}
        />

        <AgentSection node={n} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* LEFT: обзор + движок */}
          <div className="space-y-3">
            <Panel title="Обзор" icon="📊">
              <div className="grid grid-cols-3 gap-2">
                <BigStat label="коннекты" value={online ? String(eng.conns ?? 0) : "—"} accent />
                <BigStat label="аптайм" value={online ? fmtUptime(m?.uptime) : "—"} />
                <BigStat label="сервис" value={eng.active ? "работает" : online ? "стоит" : "—"} tone={eng.active ? "up" : "down"} />
              </div>
              <div className="flex items-center gap-3 mt-2 text-[11px] text-zinc-500 flex-wrap">
                {engineVer && <span>движок <b className="text-zinc-300">v{engineVer}</b></span>}
                {m?.version && <span>агент <b className="text-zinc-300">v{m.version}</b></span>}
                {sys.kernel && <span>ядро <span className="font-mono">{sys.kernel}</span></span>}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                <ActBtn onClick={doDeploy} busy={busy === "deploy"} label="🚀 Развернуть / переразвернуть" primary />
                <ActBtn onClick={doRestart} busy={busy === "restart"} label="⟳ Рестарт" />
                <ActBtn onClick={doLogs} busy={busy === "logs"} label="📋 Логи" />
                <ActBtn onClick={doUninstall} busy={busy === "uninstall"} label="🧹 Снести" danger />
              </div>
            </Panel>

            <Panel title="Движок" icon="⚙️">
              <dl className="text-sm space-y-1.5">
                <KV k="движок" v={ENGINE_LABEL[n.engine] ?? n.engine} />
                <KV k="порт" v={`:${n.listen_port}`} />
                {n.cloak_domain && <KV k="cloak SNI" v={<span className="font-mono text-xs">{n.cloak_domain}</span>} />}
                <KV k="установлен" v={eng.installed ? "да" : "нет"} />
                {n.geo_allow_countries && <KV k="гео-лок" v={<span className="font-mono text-xs">{n.geo_allow_countries}</span>} />}
              </dl>
            </Panel>

            <GeoPanel node={n} online={!!online} />
          </div>

          {/* RIGHT: система + источники коннектов (большая колонка, скролл) */}
          <div className="space-y-3">
          <Panel title="Система" icon="🖥" badge={sys.os ? sys.os.split(" ")[0] : undefined}>
            {!online ? (
              <div className="text-sm text-zinc-500">Агент не на связи — метрики недоступны.</div>
            ) : (
              <div className="space-y-3">
                {sys.cpu_model && (
                  <div className="text-[11px] text-zinc-500">
                    CPU: <span className="text-zinc-300">{sys.cpu_model}</span> · {sys.cpu_cores} ядра
                  </div>
                )}
                <Meter
                  label="Память"
                  pct={sys.mem_pct}
                  sub={`${fmtKB(sys.mem_used_kb)} / ${fmtKB(sys.mem_total_kb)}`}
                />
                <Meter
                  label="CPU"
                  pct={sys.cpu_pct}
                  sub={`load ${sys.load1 ?? "—"}`}
                />
                <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] px-3 py-2">
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-wide text-zinc-600 mb-1">
                    <span>Сеть</span>
                    <span className="font-mono normal-case">{sys.iface ?? "—"}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-emerald-400">↓ {fmtBps(sys.rx_bps)}</span>
                      <div className="text-[10px] text-zinc-600">всего {fmtBytes(sys.rx_total)}</div>
                    </div>
                    <div>
                      <span className="text-sky-400">↑ {fmtBps(sys.tx_bps)}</span>
                      <div className="text-[10px] text-zinc-600">всего {fmtBytes(sys.tx_total)}</div>
                    </div>
                  </div>
                </div>
                <div className="text-[10px] text-zinc-600 text-right">
                  обновляется в реалтайме · {metrics.isFetching ? "…" : "live"}
                </div>
              </div>
            )}
          </Panel>
          <ConnSourcesPanel nodeId={n.id} online={!!online} role={n.role} />
          </div>
        </div>

        {online && <HistoryPanel nodeId={n.id} />}

        {logs !== null && (
          <Panel title="Логи сервиса" icon="📋">
            <pre className="text-[11px] font-mono text-zinc-300 bg-black/40 border border-white/[0.06] rounded-lg p-3 overflow-auto max-h-72 whitespace-pre-wrap">
              {logs}
            </pre>
          </Panel>
        )}
      </div>
    </Modal>
  );
}

interface HistPoint { ts: string; conns: number; mem_pct: number | null; cpu_pct: number | null; rx_bps: number; tx_bps: number }
const HIST_METRICS: { key: string; label: string; color: string; get: (p: HistPoint) => number; fmt: (v: number) => string }[] = [
  { key: "conns", label: "Коннекты", color: "#2dd4bf", get: (p) => p.conns, fmt: (v) => `${v}` },
  { key: "traffic", label: "Трафик ↓", color: "#34d399", get: (p) => (p.rx_bps * 8) / 1e6, fmt: (v) => `${v.toFixed(1)}M` },
  { key: "mem", label: "RAM %", color: "#a78bfa", get: (p) => p.mem_pct ?? 0, fmt: (v) => `${Math.round(v)}%` },
  { key: "cpu", label: "CPU %", color: "#f59e0b", get: (p) => p.cpu_pct ?? 0, fmt: (v) => `${Math.round(v)}%` },
];

export function HistoryPanel({ nodeId }: { nodeId: number }) {
  const [metric, setMetric] = useState("conns");
  const q = useQuery<{ points: HistPoint[] }>({
    queryKey: ["node-history", nodeId],
    queryFn: () => api(`/api/admin/nodes/${nodeId}/history?minutes=60`),
    refetchInterval: 15000,
  });
  const m = HIST_METRICS.find((x) => x.key === metric)!;
  const data = (q.data?.points ?? []).map((p) => ({
    t: new Date(p.ts).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }),
    v: m.get(p),
  }));

  return (
    <Panel title="История (60 мин)" icon="📈" badge={q.data?.points?.length ? undefined : "копится"}>
      <div className="inline-flex items-center gap-1 mb-2 p-0.5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
        {HIST_METRICS.map((x) => (
          <button key={x.key} onClick={() => setMetric(x.key)} className={`h-6 px-2 rounded text-[11px] transition-all ${metric === x.key ? "bg-brand-soft text-brand" : "text-zinc-400 hover:text-zinc-200"}`}>
            {x.label}
          </button>
        ))}
      </div>
      {data.length < 2 ? (
        <div className="text-xs text-zinc-600 py-8 text-center">История ещё копится (сэмпл раз в минуту).</div>
      ) : (
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id={`hg-${nodeId}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={m.color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={m.color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="t" tick={{ fill: "#64748b", fontSize: 10 }} stroke="#1e2430" interval="preserveStartEnd" minTickGap={40} />
              <YAxis tick={{ fill: "#64748b", fontSize: 10 }} stroke="#1e2430" width={40} tickFormatter={m.fmt} />
              <Tooltip contentStyle={{ backgroundColor: "#0f1218", border: "1px solid rgba(148,163,184,0.18)", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#e6e9ef" }} formatter={(v) => [m.fmt(Number(v)), m.label]} />
              <Area type="monotone" dataKey="v" stroke={m.color} strokeWidth={2} fill={`url(#hg-${nodeId})`} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </Panel>
  );
}

function ConnSourcesPanel({ nodeId, online, role }: { nodeId: number; online: boolean; role: string }) {
  const q = useQuery<{ ok?: boolean; sources?: { ip: string; count: number; country?: string }[]; by_country?: { country: string; count: number }[]; total?: number }>({
    queryKey: ["node-conns", nodeId],
    queryFn: () => api(`/api/admin/nodes/${nodeId}/connections`),
    enabled: online,
    refetchInterval: online ? 8000 : false,
  });
  const byC = q.data?.by_country ?? [];
  const src = q.data?.sources ?? [];
  return (
    <Panel title="Источники коннектов" icon="🌐" badge={q.data?.total != null ? `${q.data.total}` : undefined}>
      {!online ? (
        <div className="text-sm text-zinc-500">Агент оффлайн.</div>
      ) : role === "exit" ? (
        <div className="text-xs text-zinc-500 leading-relaxed">
          Это выход — сюда коннектится только вход-нода (клиентов не видно).
          Реальные источники смотри на entry-ноде.
        </div>
      ) : src.length === 0 ? (
        <div className="text-sm text-zinc-600">Нет активных коннектов.</div>
      ) : (
        <div className="space-y-2">
          {byC.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {byC.slice(0, 8).map((c) => (
                <span key={c.country} className="text-[11px] px-2 py-0.5 rounded-md bg-white/[0.05] text-zinc-300">
                  {flag(c.country !== "??" ? c.country : null)} {c.country} · <b>{c.count}</b>
                </span>
              ))}
            </div>
          )}
          <div className="rounded-lg border border-white/[0.06] overflow-hidden max-h-[460px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[#141922] text-[10px] uppercase tracking-wide text-zinc-500">
                <tr>
                  <th className="text-left px-2.5 py-1.5">IP</th>
                  <th className="text-left px-2.5 py-1.5">гео</th>
                  <th className="text-right px-2.5 py-1.5">конн</th>
                </tr>
              </thead>
              <tbody>
                {src.map((s) => (
                  <tr key={s.ip} className="border-t border-white/[0.04]">
                    <td className="px-2.5 py-1 font-mono text-zinc-300">{s.ip}</td>
                    <td className="px-2.5 py-1 text-zinc-500">{flag(s.country && s.country !== "??" ? s.country : null)} {s.country}</td>
                    <td className="px-2.5 py-1 text-right tabular-nums text-brand">{s.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Panel>
  );
}

function GeoPanel({ node: n, online }: { node: Node; online: boolean }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const st = useQuery<{ ok?: boolean; output?: string; error?: string }>({
    queryKey: ["node-geo", n.id],
    queryFn: () => api(`/api/admin/nodes/${n.id}/geo`),
    enabled: online,
  });
  const out = st.data?.output ?? "";
  const kv = (k: string) => (out.match(new RegExp(`${k}=([^\\n]+)`)) || [])[1];
  const state = kv("STATE");
  const cidrs = kv("CIDRS");
  const drops = kv("DROPS");

  const act = async (action: "apply" | "clear") => {
    setBusy(action); setMsg(null);
    try {
      const r = await api<{ ok: boolean; error?: string; output?: string }>(`/api/admin/nodes/${n.id}/geo/${action}`, { method: "POST" });
      setMsg(r.ok ? (r.output || "готово") : `Ошибка: ${r.error}`);
      st.refetch();
    } catch (e) { setMsg(`Ошибка: ${(e as Error).message}`); }
    finally { setBusy(null); }
  };

  return (
    <Panel title="Гео-фильтр" icon="🌍" badge={state === "active" ? "вкл" : "выкл"}>
      <div className="text-xs text-zinc-500 mb-2 leading-relaxed">
        iptables+ipset: пускать только источники из разрешённых стран (CIDR c ipdeny.com).
      </div>
      <dl className="text-sm space-y-1.5 mb-3">
        <KV k="разрешены" v={n.geo_allow_countries ? <span className="font-mono text-xs">{n.geo_allow_countries}</span> : <span className="text-zinc-600">не заданы</span>} />
        <KV k="статус" v={
          !online ? <span className="text-zinc-500">агент оффлайн</span>
          : state === "active" ? <span className="text-emerald-300">включён</span>
          : state === "off" ? <span className="text-zinc-400">выключен</span>
          : <span className="text-zinc-500">{state ?? "…"}</span>
        } />
        {state === "active" && <KV k="CIDR в списке" v={<b className="text-zinc-300">{cidrs}</b>} />}
        {state === "active" && drops && drops !== "0" && <KV k="дропов" v={<b className="text-amber-300">{drops}</b>} />}
      </dl>
      {msg && <div className="text-[11px] text-zinc-400 mb-2 font-mono">{msg}</div>}
      <div className="flex gap-1.5">
        <ActBtn onClick={() => act("apply")} busy={busy === "apply"} label="Применить" primary />
        {state === "active" && <ActBtn onClick={() => act("clear")} busy={busy === "clear"} label="Снять" danger />}
      </div>
      {!n.geo_allow_countries && (
        <p className="text-[11px] text-amber-400/70 mt-2">Задай страны в «Редактировать ноду» → гео-лок (напр. RU,BY,KZ).</p>
      )}
    </Panel>
  );
}

export function Panel({ title, icon, badge, children }: { title: string; icon?: string; badge?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl card-elev p-3.5">
      <div className="flex items-center gap-2 mb-3">
        {icon && <span>{icon}</span>}
        <span className="text-xs uppercase tracking-wider text-zinc-400 font-medium">{title}</span>
        {badge && <span className="ml-auto text-[10px] uppercase text-zinc-500 border border-white/[0.08] rounded px-1.5 py-0.5">{badge}</span>}
      </div>
      {children}
    </div>
  );
}

function BigStat({ label, value, accent, tone }: { label: string; value: string; accent?: boolean; tone?: "up" | "down" }) {
  const c = tone === "up" ? "text-emerald-300" : tone === "down" ? "text-zinc-400" : accent ? "text-brand" : "text-zinc-100";
  return (
    <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] px-2.5 py-2">
      <div className="text-[10px] uppercase tracking-wide text-zinc-600">{label}</div>
      <div className={`text-lg font-bold tabular-nums leading-tight ${c}`}>{value}</div>
    </div>
  );
}

export function fmtBpsShort(bps?: number | null): string {
  if (bps == null) return "0";
  const bits = bps * 8;
  if (bits >= 1e6) return `${(bits / 1e6).toFixed(1)}M`;
  if (bits >= 1e3) return `${Math.round(bits / 1e3)}K`;
  return `${bits}`;
}

function MiniBar({ label, pct }: { label: string; pct?: number | null }) {
  const p = Math.max(0, Math.min(100, pct ?? 0));
  const color = p >= 85 ? "bg-rose-500" : p >= 60 ? "bg-amber-400" : "bg-brand";
  return (
    <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] px-2 py-1">
      <div className="flex items-center justify-between text-[9px] uppercase tracking-wide text-zinc-600">
        <span>{label}</span>
        <span className="tabular-nums text-zinc-400">{pct != null ? `${Math.min(100, Math.round(pct))}%` : "—"}</span>
      </div>
      <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden mt-1">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${p}%` }} />
      </div>
    </div>
  );
}

export function Meter({ label, pct, sub }: { label: string; pct?: number; sub?: string }) {
  const p = Math.max(0, Math.min(100, pct ?? 0));
  const color = p >= 85 ? "bg-rose-500" : p >= 60 ? "bg-amber-400" : "bg-brand";
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-zinc-400">{label}</span>
        <span className="text-zinc-300 tabular-nums">{pct != null ? `${pct}%` : "—"}{sub ? ` · ${sub}` : ""}</span>
      </div>
      <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${p}%` }} />
      </div>
    </div>
  );
}

export function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-zinc-500">{k}</dt>
      <dd className="text-zinc-200 text-right">{v}</dd>
    </div>
  );
}

function ActBtn({ onClick, busy, label, primary, danger }: { onClick: () => void; busy: boolean; label: string; primary?: boolean; danger?: boolean }) {
  const cls = danger
    ? "text-rose-400 border-rose-700/40 hover:bg-rose-500/10"
    : primary
      ? "text-brand border-brand/30 bg-brand/10 hover:bg-brand-grad hover:text-brand-fg hover:border-transparent"
      : "text-zinc-300 border-white/[0.08] hover:bg-white/[0.05]";
  return (
    <button onClick={onClick} disabled={busy} className={`h-8 px-3 text-xs font-medium rounded-lg border transition-all disabled:opacity-40 ${cls}`}>
      {busy ? "…" : label}
    </button>
  );
}


function AgentSection({ node: n }: { node: Node }) {
  const [cmd, setCmd] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [manual, setManual] = useState(false);
  const qc = useQueryClient();

  const fetchCmd = async () => {
    setLoading(true);
    try {
      const r = await api<{ install_cmd: string }>(`/api/admin/nodes/${n.id}/bootstrap`);
      setCmd(r.install_cmd);
    } catch (e) {
      setCmd(`Ошибка: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  const install = useMutation<{
    ok: boolean;
    installed?: boolean;
    connected?: boolean;
    note?: string | null;
    error?: string;
    log?: string;
    exit?: number;
    started?: boolean;
    message?: string;
  }>({
    mutationFn: () => api(`/api/admin/nodes/${n.id}/install-agent`, { method: "POST" }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["nodes"] });
      qc.invalidateQueries({ queryKey: ["node-metrics"] });
      if (r?.ok === false)
        popup({ tone: "error", title: "Установка агента", body: r.error ?? "не удалось (проверь SSH-ключ ноды)" });
      else
        popup({ tone: "success", title: "Установка агента", body: r?.message ?? "Запущена — агент появится онлайн через ~30-60с." });
    },
  });

  if (n.agent_online) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-brand-soft/40 border border-brand/20 px-3 py-2 text-sm">
        <span className="w-2 h-2 rounded-full bg-brand shadow-[0_0_8px_2px_rgba(20,184,166,.5)]" />
        <span className="text-brand font-medium">Агент онлайн</span>
        {n.agent_version && <span className="text-zinc-500 text-xs">v{n.agent_version}</span>}
        <span className="text-zinc-600 text-xs ml-auto hidden sm:inline">управление через агент, без SSH</span>
        <button
          onClick={async () => {
            if (await confirmPopup({
              title: "Обновить агент?",
              body: `${n.name}: переустановит бинарь агента (по SSH) до актуальной версии и переподключит. Движок/трафик не трогаются.`,
              confirmLabel: "Обновить",
            }))
              install.mutate();
          }}
          disabled={install.isPending}
          className="h-7 px-2.5 text-xs rounded-lg border border-white/[0.12] text-zinc-300 hover:bg-white/[0.06] hover:text-white transition-colors disabled:opacity-50"
          title="переустановить бинарь агента по SSH (обновление версии)"
        >
          {install.isPending ? "Обновляю…" : "⟳ Обновить агент"}
        </button>
      </div>
    );
  }

  // Разводим два состояния: агент никогда не подключался vs подключался ранее,
  // но связь потеряна (был last_seen). Во втором случае действие — переустановка
  // (обновит бинарь и переподключит), а не «первичная установка».
  const hadAgent = !!n.last_seen;
  const lastSeen = n.last_seen ? new Date(n.last_seen).toLocaleString("ru-RU") : null;
  // Потеря связи с уже установленным агентом — это офлайн ТЕЛЕМЕТРИИ, не «сервис
  // лёг» (сервис мог остаться жив, проверка по SSH — в блоке «Состояние»). Красим
  // нейтрально-серым, не тревожным янтарным. Янтарный — только «не установлен».
  const box = hadAgent
    ? "bg-white/[0.03] border-white/10"
    : "bg-amber-500/5 border-amber-500/15";
  const dot = hadAgent ? "bg-zinc-500" : "bg-amber-500";
  const title = hadAgent ? "text-zinc-300" : "text-amber-300";
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${box}`}>
      <div className="flex items-center gap-2 text-sm mb-2">
        <span className={`w-2 h-2 rounded-full ${dot}`} />
        <span className={`font-medium ${title}`}>
          {hadAgent ? "Агент не на связи · телеметрия недоступна" : "Агент не установлен"}
        </span>
        <button
          onClick={() => setManual((v) => !v)}
          className="ml-auto text-xs text-zinc-500 hover:text-zinc-300"
        >
          {manual ? "← по SSH" : "команда для установки вручную"}
        </button>
      </div>

      {!manual ? (
        <>
          <p className="text-xs text-zinc-500 leading-relaxed mb-2.5">
            {hadAgent ? (
              <>
                Агент был установлен и ранее выходил на связь{lastSeen ? <> (последняя связь: {lastSeen})</> : null},
                но сейчас недоступен. Переустановка обновит бинарь агента и переподключит его к панели.
              </>
            ) : (
              <>
                Панель подключится к ноде по SSH собственным ключом и установит агента. Необходим только
                публичный ключ панели (кнопка <b>🔑 SSH-ключ</b> вверху) в{" "}
                <code className="text-zinc-400">~/.ssh/authorized_keys</code> ноды.
              </>
            )}
          </p>
          <Button onClick={() => install.mutate()} disabled={install.isPending}>
            {install.isPending
              ? (hadAgent ? "Переустанавливаю…" : "Устанавливаю…")
              : (hadAgent ? "⟳ Переустановить и переподключить" : "⚡ Установить по SSH")}
          </Button>
          {install.data && (() => {
            const d = install.data;
            // 3 честных состояния: на связи / установлен-но-не-достучался / ошибка install
            const connected = d.connected;
            const installedNotConnected = d.installed && !d.connected;
            const tone = connected
              ? "bg-brand-soft/40 border border-brand/20 text-brand"
              : installedNotConnected
                ? "bg-amber-500/10 border border-amber-500/25 text-amber-300"
                : "bg-rose-500/10 border border-rose-500/20 text-rose-300";
            const title = connected
              ? "✓ Агент на связи с панелью"
              : installedNotConnected
                ? "⚠ Установлен, но НЕ достучался до панели"
                : `✗ Ошибка установки${d.exit != null ? ` (exit ${d.exit})` : ""}`;
            return (
              <div className={`mt-2 rounded-md px-3 py-2 text-xs ${tone}`}>
                <div className="font-medium mb-1">{title}</div>
                {installedNotConnected && d.note && (
                  <div className="text-amber-200/80 mb-1 leading-relaxed">{d.note}</div>
                )}
                {(d.error || d.log) && (
                  <pre className="font-mono text-[10px] text-zinc-400 whitespace-pre-wrap break-all max-h-32 overflow-auto">
                    {d.error || d.log}
                  </pre>
                )}
              </div>
            );
          })()}
          {install.isError && (
            <div className="mt-2 rounded-md px-3 py-2 text-xs bg-rose-500/10 border border-rose-500/20 text-rose-300">
              {(install.error as Error).message}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center mb-2">
            <button onClick={fetchCmd} disabled={loading} className="text-xs text-brand hover:underline">
              {loading ? "…" : cmd ? "обновить команду" : "показать команду установки"}
            </button>
          </div>
          <p className="text-xs text-zinc-500 leading-relaxed mb-2">
            Запасной путь: вставь команду на VPS вручную (под root). Агент поставится,
            сам достучится до панели.
          </p>
        </>
      )}
      {manual && cmd && (
        <div className="flex gap-2 items-stretch">
          <pre className="flex-1 px-2.5 py-2 rounded-md bg-black/40 border border-white/[0.08] font-mono text-[11px] text-zinc-200 overflow-auto max-h-28 whitespace-pre-wrap break-all">
            {cmd}
          </pre>
          <Button
            variant="ghost"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(cmd);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              } catch {
                /* ignore */
              }
            }}
          >
            {copied ? "✓" : "Копи"}
          </Button>
        </div>
      )}
    </div>
  );
}

function MenuItem({
  children,
  onClick,
  danger = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3.5 py-2 transition-colors ${
        danger ? "text-rose-400 hover:bg-rose-500/10" : "text-zinc-300 hover:bg-white/[0.05]"
      }`}
    >
      {children}
    </button>
  );
}

function NodeForm({
  node,
  onDone,
  onCancel,
}: {
  node: Node | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const edit = !!node;
  const [name, setName] = useState(node?.name ?? "");
  const [host, setHost] = useState(node?.host ?? "");
  const [country, setCountry] = useState(node?.country ?? "");
  const [role, setRole] = useState(node?.role ?? "exit");
  const [engine, setEngine] = useState(node?.engine ?? "mtproto_zig");
  const [listenPort, setListenPort] = useState(String(node?.listen_port ?? 443));
  const [cloak, setCloak] = useState(node?.cloak_domain ?? "");
  const [sshUser, setSshUser] = useState(node?.ssh_user ?? "root");
  const [sshPort, setSshPort] = useState(String(node?.ssh_port ?? 22));
  const [maxConn, setMaxConn] = useState(String(node?.max_connections ?? 60000));
  const [geo, setGeo] = useState(node?.geo_allow_countries ?? "");
  const [tags, setTags] = useState(node?.tags ?? "");
  const [note, setNote] = useState(node?.note ?? "");

  const body = () => ({
    name,
    host,
    country: country || null,
    role,
    engine,
    listen_port: Number(listenPort) || 443,
    cloak_domain: cloak || null,
    ssh_user: sshUser || "root",
    ssh_port: Number(sshPort) || 22,
    max_connections: Number(maxConn) || 60000,
    geo_allow_countries: geo || null,
    tags: tags || null,
    note: note || null,
  });

  const save = useMutation({
    mutationFn: () =>
      edit
        ? api(`/api/admin/nodes/${node!.id}`, { method: "PATCH", body: JSON.stringify(body()) })
        : api("/api/admin/nodes", { method: "POST", body: JSON.stringify(body()) }),
    onSuccess: onDone,
  });

  const createInstall = useMutation({
    mutationFn: async () => {
      const created = await api<{ id: number }>("/api/admin/nodes", {
        method: "POST",
        body: JSON.stringify(body()),
      });
      const r = await api<{ ok: boolean; installed?: boolean; connected?: boolean; note?: string; error?: string; log?: string; exit?: number }>(
        `/api/admin/nodes/${created.id}/install-agent`,
        { method: "POST" },
      );
      return r;
    },
    onSuccess: (r) => {
      if (r.connected) popup({ tone: "success", title: "Нода создана", body: "Агент на связи с панелью ✓" });
      else if (r.installed) popup({ tone: "warning", title: "Агент установлен, но не достучался", body: r.note || "нода не выпускает исходящий к панели" });
      else popup({ tone: "error", title: "Нода создана, агент не встал", body: r.error || r.log || "см. логи" });
      onDone();
    },
    onError: (e) => popup({ tone: "error", title: "Установка агента", body: (e as Error).message }),
  });

  const busy = save.isPending || createInstall.isPending;
  const invalid = !name.trim() || !host.trim();

  return (
    <div className="space-y-5">
      <FormSection title="Основное">
        <div className="grid grid-cols-2 gap-3">
          <Field label="имя">
            <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </Field>
          <Field label="host / IP">
            <Input value={host} onChange={(e) => setHost(e.target.value)} className="font-mono" />
          </Field>
          <Field label="страна">
            <Select
              value={country}
              onChange={setCountry}
              options={["", ...COUNTRIES]}
              render={(c) => (c ? `${flag(c)} ${c}` : "— не задана —")}
            />
          </Field>
          <Field label="роль">
            <Select
              value={role}
              onChange={(r) => {
                setRole(r);
                if (ROLE_DEFAULT_ENGINE[r]) setEngine(ROLE_DEFAULT_ENGINE[r]);
              }}
              options={ROLES}
              render={(r) => ROLE_LABEL[r] ?? r}
            />
          </Field>
          <Field label="движок">
            <Select value={engine} onChange={setEngine} options={ENGINES} render={(e) => ENGINE_LABEL[e] ?? e} />
          </Field>
          <Field label="теги (через запятую)">
            <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="yandex, whitelist" />
          </Field>
        </div>
      </FormSection>

      <FormSection title="Транспорт">
        <div className="grid grid-cols-2 gap-3">
          <Field label="listen port">
            <Input value={listenPort} onChange={(e) => setListenPort(e.target.value)} />
          </Field>
          {ENGINE_NEEDS_CLOAK(engine) && (
            <Field label="cloak SNI (FakeTLS)">
              <Input value={cloak} onChange={(e) => setCloak(e.target.value)} placeholder="strm.yandex.net" className="font-mono" />
            </Field>
          )}
        </div>
      </FormSection>

      <FormSection title="SSH и лимиты">
        <p className="text-xs text-zinc-500 mb-3 leading-relaxed">
          Ключ вводить не нужно — панель ходит на ноды своим ключом. Просто вставь
          <b className="text-zinc-300"> публичный ключ панели</b> (кнопка «🔑 SSH-ключ» вверху)
          в <code className="text-zinc-400">/root/.ssh/authorized_keys</code> на новом сервере,
          затем укажи здесь только host + ssh user.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="ssh user">
            <Input value={sshUser} onChange={(e) => setSshUser(e.target.value)} />
          </Field>
          <Field label="ssh port">
            <Input value={sshPort} onChange={(e) => setSshPort(e.target.value)} />
          </Field>
          <Field label="max connections">
            <Input value={maxConn} onChange={(e) => setMaxConn(e.target.value)} />
          </Field>
          <Field label="гео-лок (ISO, через запятую)">
            <Input value={geo} onChange={(e) => setGeo(e.target.value)} placeholder="RU,BY,KZ" className="font-mono" />
          </Field>
        </div>
      </FormSection>

      <Field label="заметка">
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="необязательно" />
      </Field>

      {edit && node && engine !== node.engine && (
        <div className="rounded-lg bg-amber-500/10 border border-amber-500/25 px-3 py-2.5 text-xs text-amber-200/90 leading-relaxed">
          <b>Смена движка ноды.</b> При сохранении старый движок ({ENGINE_LABEL[node.engine] ?? node.engine}) будет
          снесён с ноды, чтобы не остался работать в конфликте по порту. Нода перейдёт в состояние
          «сервис не развёрнут» — потребуется повторный деплой из карточки ноды или хоста.
        </div>
      )}
      {save.isError && <div className="text-xs text-red-400">{(save.error as Error).message}</div>}
      <div className="flex gap-3 pt-1 justify-end flex-wrap">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>
          Отмена
        </Button>
        {edit ? (
          <Button variant="primary" disabled={busy || invalid} onClick={() => save.mutate()}>
            {save.isPending ? "Сохраняю…" : "Сохранить"}
          </Button>
        ) : (
          <>
            <Button variant="ghost" disabled={busy || invalid} onClick={() => save.mutate()}>
              {save.isPending ? "Создаю…" : "Только создать"}
            </Button>
            <Button variant="primary" disabled={busy || invalid} onClick={() => createInstall.mutate()}>
              {createInstall.isPending ? "Ставлю агента…" : "⚡ Создать + поставить агента"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-2 flex items-center gap-2">
        <span className="w-1 h-3.5 rounded-full bg-brand-grad" />
        {title}
      </div>
      {children}
    </div>
  );
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

export function Select({
  value,
  onChange,
  options,
  render,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  render?: (v: string) => string;
}) {
  return (
    <DarkSelect
      value={value}
      onChange={onChange}
      options={options.map((o) => ({ value: o, label: render ? render(o) : o }))}
      placeholder="— выбрать —"
    />
  );
}
